import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, ShieldAlert, ShieldCheck, Trash2, UserMinus, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();
  const canCreateUsers = isAdmin || !!user?.canCreateUsers;
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: api.users.list, enabled: isAdmin });

  const inval = () => qc.invalidateQueries({ queryKey: ["users"] });
  const setRoleMut = useMutation({ mutationFn: ({ id, role }) => api.users.setRole(id, role), onSuccess: () => { toast.success("Rol actualizat"); inval(); }, onError: (e) => toast.error(apiError(e)) });
  const createMut = useMutation({ mutationFn: api.users.create, onSuccess: () => { toast.success("Utilizator creat cu succes"); inval(); setCreateOpen(false); resetForm(); }, onError: (e) => toast.error(apiError(e)) });
  const deleteMut = useMutation({ mutationFn: (id) => api.users.remove(id), onSuccess: () => { toast.success("Utilizator șters"); inval(); }, onError: (e) => toast.error(apiError(e)) });
  const permsMut = useMutation({ mutationFn: ({ id, perms }) => api.users.setPermissions(id, perms), onSuccess: () => { toast.success("Permisiuni actualizate"); inval(); }, onError: (e) => toast.error(apiError(e)) });

  const [createOpen, setCreateOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formCanDelete, setFormCanDelete] = useState(false);
  const [formCanDownload, setFormCanDownload] = useState(true);
  const [formCanCreateUsers, setFormCanCreateUsers] = useState(false);

  const resetForm = () => { setFormName(""); setFormEmail(""); setFormPassword(""); setFormCanDelete(false); setFormCanDownload(true); setFormCanCreateUsers(false); };
  const handleCreate = () => {
    if (!formEmail || !formName || !formPassword) { toast.error("Completați toate câmpurile obligatorii"); return; }
    createMut.mutate({ email: formEmail, name: formName, password: formPassword, role: "user", canDelete: formCanDelete, canDownload: formCanDownload, canCreateUsers: formCanCreateUsers });
  };

  if (!isAdmin) {
    return <DashboardLayout><div className="max-w-2xl mx-auto mt-12"><Card><CardContent className="flex flex-col items-center gap-3 py-12 text-center"><ShieldAlert className="h-10 w-10 text-muted-foreground" /><p className="text-lg font-medium">Acces restricționat</p><p className="text-sm text-muted-foreground">Doar administratorii pot vedea lista de utilizatori.</p></CardContent></Card></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Utilizatori</h1>
            <p className="text-sm text-muted-foreground">Gestionați utilizatorii și permisiunile lor. Operatorii nu pot șterge sau crea utilizatori decât dacă li se acordă explicit aceste drepturi.</p>
          </div>
          {canCreateUsers && <Button onClick={() => setCreateOpen(true)} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" /> Adaugă utilizator</Button>}
        </div>

        <Card>
          <CardHeader><CardTitle>{users?.length ?? 0} utilizatori</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nume</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Permisiuni</TableHead><TableHead>Ultima conectare</TableHead><TableHead className="text-right">Acțiuni</TableHead></TableRow></TableHeader>
              <TableBody>
                {(users ?? []).map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name || "-"}</TableCell>
                    <TableCell>{u.email || "-"}</TableCell>
                    <TableCell>{u.role === "admin" ? <Badge variant="default" className="gap-1"><ShieldCheck className="h-3 w-3" /> Admin</Badge> : <Badge variant="secondary">Operator</Badge>}</TableCell>
                    <TableCell>
                      {u.role === "admin" ? <span className="text-xs text-muted-foreground">Toate drepturile</span> : (
                        <div className="flex flex-wrap gap-1">
                          {u.canDelete && <Badge variant="outline" className="text-[10px]">Ștergere</Badge>}
                          {u.canDownload && <Badge variant="outline" className="text-[10px]"><Download className="h-2.5 w-2.5 mr-0.5" />Descărcare</Badge>}
                          {u.canCreateUsers && <Badge variant="outline" className="text-[10px]"><UserPlus className="h-2.5 w-2.5 mr-0.5" />Creare utilizatori</Badge>}
                          {!u.canDelete && !u.canDownload && !u.canCreateUsers && <span className="text-[10px] text-muted-foreground">Doar vizualizare</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{u.lastSignedIn ? new Date(u.lastSignedIn).toLocaleString("ro-RO") : "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {u.role === "admin" ? (
                          <Button variant="outline" size="sm" disabled={u.id === user?.id || setRoleMut.isPending} onClick={() => setRoleMut.mutate({ id: u.id, role: "user" })}><UserMinus className="h-3.5 w-3.5 mr-1" /> Retrogradează</Button>
                        ) : (
                          <>
                            <Button variant="default" size="sm" disabled={setRoleMut.isPending} onClick={() => setRoleMut.mutate({ id: u.id, role: "admin" })}><UserPlus className="h-3.5 w-3.5 mr-1" /> Admin</Button>
                            <PermissionsDialog user={u} onUpdate={(perms) => permsMut.mutate({ id: u.id, perms })} isPending={permsMut.isPending} />
                          </>
                        )}
                        {u.id !== user?.id && <Button variant="destructive" size="sm" disabled={deleteMut.isPending} onClick={() => { if (confirm(`Sigur doriți să ștergeți utilizatorul ${u.name || u.email}?`)) deleteMut.mutate(u.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Adaugă utilizator nou</DialogTitle><DialogDescription>Creați un cont local cu email și parolă. Selectați drepturile pe care doriți să le acordați.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label htmlFor="name">Nume complet *</Label><Input id="name" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Ion Popescu" data-testid="user-name-input" /></div>
            <div className="space-y-2"><Label htmlFor="email">Email *</Label><Input id="email" type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="ion@exemplu.ro" data-testid="user-email-input" /></div>
            <div className="space-y-2"><Label htmlFor="password">Parolă *</Label><Input id="password" type="password" value={formPassword} onChange={(e) => setFormPassword(e.target.value)} placeholder="Minim 6 caractere" data-testid="user-password-input" /></div>
            <div className="space-y-3 pt-2">
              <Label className="text-sm font-medium">Drepturi</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2"><Checkbox id="perm-delete" checked={formCanDelete} onCheckedChange={(v) => setFormCanDelete(!!v)} /><Label htmlFor="perm-delete" className="text-sm font-normal cursor-pointer">Drept de ștergere (clipuri, filme, evenimente, camere)</Label></div>
                <div className="flex items-center gap-2"><Checkbox id="perm-download" checked={formCanDownload} onCheckedChange={(v) => setFormCanDownload(!!v)} /><Label htmlFor="perm-download" className="text-sm font-normal cursor-pointer">Drept de descărcare (filme MP4 concatenate)</Label></div>
                <div className="flex items-center gap-2"><Checkbox id="perm-create-users" checked={formCanCreateUsers} onCheckedChange={(v) => setFormCanCreateUsers(!!v)} /><Label htmlFor="perm-create-users" className="text-sm font-normal cursor-pointer">Drept de creare utilizatori noi</Label></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Anulează</Button>
            <Button onClick={handleCreate} disabled={createMut.isPending} data-testid="save-user-btn">{createMut.isPending ? "Se creează..." : "Creează utilizator"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function PermissionsDialog({ user: u, onUpdate, isPending }) {
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(u.canDelete);
  const [dl, setDl] = useState(u.canDownload);
  const [cu, setCu] = useState(u.canCreateUsers);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setDel(u.canDelete); setDl(u.canDownload); setCu(u.canCreateUsers); setOpen(true); }}>Drepturi</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Editare drepturi</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2"><Checkbox id="ed-del" checked={del} onCheckedChange={(v) => setDel(!!v)} /><Label htmlFor="ed-del" className="text-sm font-normal cursor-pointer">Drept de ștergere</Label></div>
            <div className="flex items-center gap-2"><Checkbox id="ed-dl" checked={dl} onCheckedChange={(v) => setDl(!!v)} /><Label htmlFor="ed-dl" className="text-sm font-normal cursor-pointer">Drept de descărcare</Label></div>
            <div className="flex items-center gap-2"><Checkbox id="ed-cu" checked={cu} onCheckedChange={(v) => setCu(!!v)} /><Label htmlFor="ed-cu" className="text-sm font-normal cursor-pointer">Drept de creare utilizatori</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={() => { onUpdate({ canDelete: del, canDownload: dl, canCreateUsers: cu }); setOpen(false); }} disabled={isPending}>{isPending ? "Se salvează..." : "Salvează"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
